/**
 * Frontmatter reader for the policy documents in this directory.
 *
 * Deliberately tiny, and deliberately not a YAML parser. These files are read at
 * module load on every page that can show a policy, and the frontmatter is seven
 * single-line scalars, so pulling in a YAML dependency to read them would cost
 * more than it explains. The trade is that block scalars, nesting and multi-line
 * values are unsupported — `README.md` says so, and every unreadable file throws
 * here at import time rather than rendering a policy with a missing field.
 */

export type PolicyStatus = "draft" | "approved";

export interface PolicyDocument {
  id: string;
  version: number;
  effectiveDate: string;
  status: PolicyStatus;
  /** Whether adopting this version required users to accept again. */
  materialChange: boolean;
  title: string;
  /** The tick box wording shown in the consent dialog. */
  acknowledgement: string;
  /** Markdown, frontmatter removed. */
  body: string;
}

const FRONTMATTER = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/;

function requireField(fields: Map<string, string>, key: string): string {
  const value = fields.get(key);
  if (value === undefined || value === "") {
    throw new Error(`Policy document is missing required field "${key}"`);
  }
  return value;
}

export function parsePolicyDocument(raw: string): PolicyDocument {
  const match = FRONTMATTER.exec(raw);
  if (!match) {
    throw new Error("Policy document has no frontmatter block");
  }

  const fields = new Map<string, string>();
  for (const line of match[1].split(/\r?\n/)) {
    if (line.trim() === "") continue;
    // Split on the first colon only, so a title may contain one.
    const separator = line.indexOf(":");
    if (separator === -1) {
      throw new Error(`Policy frontmatter line is not "key: value": ${line}`);
    }
    fields.set(
      line.slice(0, separator).trim(),
      line.slice(separator + 1).trim(),
    );
  }

  const rawVersion = requireField(fields, "version");
  if (!/^[1-9]\d*$/.test(rawVersion)) {
    throw new Error(
      `Policy version must be a positive integer, got "${rawVersion}"`,
    );
  }

  const rawStatus = requireField(fields, "status");
  if (rawStatus !== "draft" && rawStatus !== "approved") {
    throw new Error(
      `Policy status must be "draft" or "approved", got "${rawStatus}"`,
    );
  }

  const rawMaterialChange = requireField(fields, "material_change");
  if (rawMaterialChange !== "true" && rawMaterialChange !== "false") {
    throw new Error(
      `Policy material_change must be true or false, got "${rawMaterialChange}"`,
    );
  }

  return {
    id: requireField(fields, "id"),
    version: Number(rawVersion),
    effectiveDate: requireField(fields, "effective_date"),
    status: rawStatus,
    materialChange: rawMaterialChange === "true",
    title: requireField(fields, "title"),
    acknowledgement: requireField(fields, "acknowledgement"),
    body: raw.slice(match[0].length).trim(),
  };
}
